// PRODUCTION — flow editor sticky page header (title, save, revert)
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import tw from 'twin.macro'
import { EditMinor } from '@shopify/polaris-icons'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip } from '@mui/material'
import { Button, iconStyles } from 'src/components/shared/Button'
import { EditTemplateTitleModal } from 'src/components/flow-editor/edit-template-title/EditTemplateTitleModal'
import type { EditTemplateTitleFormValues } from 'src/components/flow-editor/edit-template-title/EditTemplateTitleForm.interface'
import { AdminRenameOnboardingTemplate, FlowEditorApplyOp } from 'src/constants/actionTypes'
import { buildCustomerTabPath } from 'src/pages/admin-dashboard/adminDashboardNav'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getOnboardingTemplateSaving } from 'src/selectors/onboardingTemplateList'
import { RevertChangesModal } from 'src/components/flow-editor/FlowModals'
import { FlowEditorChangesModal } from 'src/components/flow-editor/preview/FlowEditorChangesModal'
import { FLOW_EDITOR_FEATURE_FLAGS } from 'src/constants/flowEditorFeatureFlags'
import { useFlowEditorContext } from 'src/pages/admin/flow-editor/context/useFlowEditorContext'
import { NavigationGuardContext } from 'src/components/layout/NavigationGuard.context'

/** Shown in place of the heading while a new template has no name yet — never persisted. */
const UNTITLED_TEMPLATE_PLACEHOLDER = 'Untitled template'

export function FlowEditorHeader() {
  const [showRevertModal, setShowRevertModal] = useState(false)
  const [showSaveModal, setShowSaveModal] = useState(false)

  const { customerId = '', templateId = '' } = useParams<{
    customerId: string
    templateId: string
  }>()
  const { setGuardedNavigate } = useContext(NavigationGuardContext)
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  // The rename request drives the onboardingTemplateList slice's `saving`, not the editor's —
  // the editor's flag belongs to the step/group actions PUT.
  const isRenaming = useSelector(getOnboardingTemplateSaving)
  const dispatch = useDispatch()
  // Rename (PUT …/:id/name) and Save (PUT …/:id, or POST in create mode) are
  // `collect_data_templates_manage`. Without it the editor stays explorable — local edits and
  // Revert still work, the review preview is a view call — but nothing can be persisted. Create
  // mode already requires manage at the route, so this only ever hides controls in edit mode.
  const canManage = useHasPermission(InternalPermission.CollectDataTemplatesManage)

  const {
    error,
    flowName,
    guardNavigate,
    handleRevert,
    handleSave,
    isCreateMode,
    isDirty,
    justSaved,
    saveBlockedMessage,
    saving,
    template,
  } = useFlowEditorContext()

  const [showTitleModal, setShowTitleModal] = useState(false)
  // Whether the create-mode prompt has already been opened for the current visit to `/new`.
  const hasPromptedForNameRef = useRef(false)
  const promptedForCustomerRef = useRef<string | null>(null)

  const saveEnabled = isDirty && !saveBlockedMessage && !justSaved

  // New-template mode opens on the name prompt: the name travels in the create request, so there
  // is nothing to save until it is set. This reacts to create mode rather than reading it once at
  // mount, because `/new` and `/:templateId` render the same element — switching between them
  // (browser Back right after a create, for one) does not remount this header. The ref keeps it to
  // one automatic prompt per visit, so reverting to an empty name does not reopen the modal the
  // user just dismissed, and edit mode never opens it on its own.
  useEffect(() => {
    if (!isCreateMode) {
      hasPromptedForNameRef.current = false

      return
    }

    // Two customers' `/new` URLs match the same route pattern, so moving between them does not
    // remount this header — the same reason a post-create Back does not. Reset on the customer so
    // the second customer's empty draft still gets its prompt.
    if (promptedForCustomerRef.current !== customerId) {
      promptedForCustomerRef.current = customerId
      hasPromptedForNameRef.current = false
    }

    if (hasPromptedForNameRef.current || flowName.trim() !== '') {
      return
    }

    hasPromptedForNameRef.current = true
    setShowTitleModal(true)
  }, [customerId, flowName, isCreateMode])

  const handleRevertConfirm = () => {
    setShowRevertModal(false)
    handleRevert()
  }

  const handleSaveConfirm = useCallback(async () => {
    await handleSave()
    setShowSaveModal(false)
  }, [handleSave])

  // The review modal lists which contractors each pending change will affect, which it reads from
  // the same preview endpoint the live-updates panel uses. That endpoint is bound to a persisted
  // template, so in new-template mode the modal can only ever say "No pending changes" over a
  // draft that is entirely pending — worse than useless. Save directly instead.
  const handleSaveClick = useCallback(() => {
    if (isCreateMode) {
      void handleSave()

      return
    }

    setShowSaveModal(true)
  }, [handleSave, isCreateMode])

  // The top bar's Back button is the only way back to the templates tab. guardNavigate shows the
  // unsaved-changes modal when the draft is dirty and navigates straight through when it is not
  // (see useUnsavedChangesGuard), so it is registered with the shell for as long as the editor
  // is mounted.
  useEffect(() => {
    setGuardedNavigate(() => guardNavigate)

    return () => {
      setGuardedNavigate(null)
    }
  }, [guardNavigate, setGuardedNavigate])

  const handleOpenTitleModal = useCallback(() => {
    setShowTitleModal(true)
  }, [])

  const handleCloseTitleModal = useCallback(() => {
    setShowTitleModal(false)

    // An unnamed new template is a dead end — cancelling the first prompt goes back to the list.
    if (isCreateMode && flowName.trim() === '') {
      guardNavigate(buildCustomerTabPath(customerId, 'onboarding-templates'))
    }
  }, [customerId, flowName, guardNavigate, isCreateMode])

  // Two modes, deliberately not unified. Editing an existing template: renaming is its own request
  // and lands immediately — outside the step/group "Save changes" diff, which cannot express a
  // template name. The reducer patches the name onto the template, the saved snapshot and the
  // draft, so that path does not make the editor dirty. Creating one: there is no id to rename yet,
  // so the name is applied to the draft as a document op and travels in the create request — which
  // does dirty the editor, exactly as intended.
  const handleSubmitTitle = useCallback(
    async (values: EditTemplateTitleFormValues) => {
      const name = values.name.trim()

      // Before the template exists the rename endpoint cannot be used, so the name goes into the
      // draft and rides along in the create request.
      if (isCreateMode) {
        dispatch(FlowEditorApplyOp({ kind: 'setTemplateName', name }))
        setShowTitleModal(false)

        return
      }

      const renamed = await asyncDispatchWithErrorToast(
        AdminRenameOnboardingTemplate.request({
          collectDataTemplateId: templateId,
          name,
        }),
      )

      if (!renamed) {
        return
      }

      setShowTitleModal(false)
    },
    [asyncDispatchWithErrorToast, dispatch, isCreateMode, templateId],
  )

  // Presentational only: it never reaches the draft, the create request or the dirty check — an
  // unnamed template stays unnamed until the user types a name.
  const showUntitledPlaceholder = isCreateMode && flowName.trim() === ''
  const titleActionLabel = isCreateMode ? 'Name template' : 'Rename template'

  return (
    <Fragment>
      <div
        css={tw`sticky top-0 z-30 -mx-6 mb-5 flex shrink-0 items-center gap-3 bg-shades-50 px-6 pb-3 pt-2`}
      >
        <div css={tw`flex min-w-0 flex-1 items-center gap-2 overflow-hidden py-1`}>
          <h1
            css={[
              tw`m-0 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-lg font-bold text-shades-900`,
              showUntitledPlaceholder && tw`font-medium text-shades-400`,
            ]}
          >
            {showUntitledPlaceholder ? UNTITLED_TEMPLATE_PLACEHOLDER : flowName}
          </h1>
          {FLOW_EDITOR_FEATURE_FLAGS.allowEditTemplateTitle && canManage && (
            <Tooltip title={titleActionLabel}>
              <span>
                <Button
                  size="tiny"
                  color="grey"
                  iconOnly
                  aria-label={titleActionLabel}
                  leftIcon={<EditMinor width={20} css={iconStyles.grey} />}
                  onClick={handleOpenTitleModal}
                  data-testid="flow-editor-rename-template-button"
                />
              </span>
            </Tooltip>
          )}
        </div>

        {error && template && (
          <span css={tw`flex-shrink-0 whitespace-nowrap text-[13px] font-medium text-critical-500`}>
            {error}
          </span>
        )}

        {/* In create mode the reason shows even when the draft is pristine: reverting resets to the
            empty scaffold, which is itself unsavable, and the user still needs to be told why. */}
        {!error && saveBlockedMessage && (isDirty || isCreateMode) && (
          <span
            css={tw`min-w-0 flex-1 break-words whitespace-normal text-[13px] font-medium leading-snug text-critical-500`}
          >
            {saveBlockedMessage}
          </span>
        )}

        {!error && !saveBlockedMessage && isDirty && !saving && (
          <span css={tw`flex-shrink-0 whitespace-nowrap text-[13px] font-medium text-shades-500`}>
            Unsaved changes
          </span>
        )}

        {isDirty && (
          <Button
            variant="outlined"
            size="small"
            color="white"
            onClick={() => setShowRevertModal(true)}
            className={tw`!border-shades-200 !text-shades-700 hover:!border-shades-300 hover:!bg-shades-50`}
          >
            Revert
          </Button>
        )}

        {canManage && (
          <Button
            size="small"
            color={saveEnabled || justSaved ? 'primary' : 'grey'}
            loading={saving}
            disabled={!isDirty || saving || Boolean(saveBlockedMessage)}
            onClick={handleSaveClick}
            className={
              justSaved
                ? tw`!bg-success-500 hover:!bg-success-500 active:!bg-success-500 [&.Mui-disabled]:!bg-success-500`
                : undefined
            }
          >
            {justSaved ? 'Saved!' : 'Save changes'}
          </Button>
        )}
      </div>

      <RevertChangesModal
        open={showRevertModal}
        onCancel={() => setShowRevertModal(false)}
        onConfirm={handleRevertConfirm}
      />

      <EditTemplateTitleModal
        isOpen={showTitleModal}
        name={flowName}
        isSaving={isCreateMode ? false : isRenaming}
        title={titleActionLabel}
        submitLabel={isCreateMode ? 'Continue' : 'Save name'}
        onSubmit={handleSubmitTitle}
        onClose={handleCloseTitleModal}
      />

      <FlowEditorChangesModal
        open={showSaveModal}
        mode="confirm"
        saving={saving}
        onClose={() => setShowSaveModal(false)}
        onConfirm={handleSaveConfirm}
      />
    </Fragment>
  )
}
